import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, Plus, ListChecks, Search, Download } from 'lucide-react';
import { RowMenu } from '../../components/RowMenu';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { LiveConsoleBreadcrumbs } from '../LiveConsoleBreadcrumbs';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Input, Skeleton, Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useLmsTranscodingPresets,
  useLmsTranscodingPresetDetail,
  useDeleteLmsTranscodingPreset,
} from '@/api/media/live-presets';
import type { LmsTranscodingPreset } from '@/api/media/live-presets-types';
import styles from './PresetsList.module.css';

// ─── Detail dialog ────────────────────────────────────────────────────────────

function PresetDetailDialog({ serviceId, presetId, open, onOpenChange }: {
  serviceId: string; presetId: string | null;
  open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const { data, isLoading } = useLmsTranscodingPresetDetail(serviceId, presetId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Preset Detail</DialogTitle></DialogHeader>
        <DialogBody>
          {isLoading && <div className={styles.detailLoadingCenter}>Loading…</div>}
          {!isLoading && data && (
            <div className={styles.detailSections}>
              <div>
                <div className={styles.detailGroupLabel}>Name</div>
                <div className={styles.detailName}>{data.name}</div>
              </div>
              {data.description && (
                <div>
                  <div className={styles.detailGroupLabel}>Description</div>
                  <div>{data.description}</div>
                </div>
              )}
              <div>
                <div className={styles.detailGridLabel}>Video</div>
                <div className={styles.detailGrid}>
                  {[['Codec', data.video.codec], ['Profile', data.video.profile], ['Bitrate', `${data.video.bitrate} kbps`], ['FPS', String(data.video.fps)], ['GOP', String(data.video.gop)], ['Resolution', `${data.video.width}×${data.video.height}`]].map(([k, v]) => (
                    <div key={k} className={styles.detailItem}>
                      <div className={styles.detailItemKey}>{k}</div>
                      <div className={styles.detailItemValue}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className={styles.detailGridLabel}>Audio</div>
                <div className={styles.detailGrid}>
                  {[['Codec', data.audio.codec], ['Bitrate', `${data.audio.bitrate} kbps`], ['Sample Rate', `${(data.audio.sample_rate / 1000).toFixed(1)} kHz`], ['Channels', String(data.audio.channels)]].map(([k, v]) => (
                    <div key={k} className={styles.detailItem}>
                      <div className={styles.detailItemKey}>{k}</div>
                      <div className={styles.detailItemValue}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

// ─── List ─────────────────────────────────────────────────────────────────────

const HIDDEN_PRESETS = new Set(['__snapshot__', 'transmux']);

/** Download the given presets as a CSV file. */
function exportCsv(rows: LmsTranscodingPreset[]) {
  const head = ['preset', 'description'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows.map((r) => [r.label || r.value, r.description].map(esc).join(',')).join('\n');
  const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `live-presets-${rows.length}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export interface LivePresetsProps {
  serviceId: string;
}

/**
 * Transcoding presets list for Live Transcoding.
 * Links: `transcoding-presets/create` and `transcoding-presets/select` (relative).
 */
export function LivePresets({ serviceId }: LivePresetsProps) {
  const navigate = useNavigate();
  const { show } = useToast();

  const { data: allPresets = [], isLoading } = useLmsTranscodingPresets(serviceId);
  const presets = allPresets.filter((p) => !HIDDEN_PRESETS.has(p.value));
  const loading = isLoading;

  const deleteMut = useDeleteLmsTranscodingPreset(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Transcoding preset deleted' }); setPendingDelete(null); },
    onError: (e) => show({ tone: 'danger', title: e.message || 'Failed to delete preset' }),
  });

  const [query, setQuery] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<LmsTranscodingPreset | null>(null);

  // Client-side search over label / description.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return presets;
    return presets.filter((p) =>
      [p.label, p.value, p.description].some((v) => (v ?? '').toLowerCase().includes(q)),
    );
  }, [presets, query]);

  const columns = useMemo<ColumnDef<LmsTranscodingPreset, unknown>[]>(() => [
    { id: 'label', header: 'Preset', cell: ({ row }) => <span className={styles.cellLabel}>{row.original.label || row.original.value}</span> },
    { id: 'description', header: 'Description', cell: ({ row }) => <span className={styles.cellMuted}>{row.original.description || '—'}</span> },
    {
      id: 'actions', header: '',
      cell: ({ row }) => (
        <RowMenu
          items={[
            { label: 'View', onSelect: () => setDetailId(row.original.value) },
            { label: 'Delete', danger: true, onSelect: () => setPendingDelete(row.original) },
          ]}
        />
      ),
    },
  ], []);

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<LiveConsoleBreadcrumbs trail={[{ label: 'Transcoding Presets' }]} />}
        title={`Transcoding Presets${presets.length ? ` (${presets.length})` : ''}`}
        description="View available live stream transcoding presets."
        actions={
          <div className={styles.headerActions}>
            <Button variant="secondary" startIcon={<ListChecks size={15} />} onClick={() => navigate('select')}>Select Presets</Button>
            <Button startIcon={<Plus size={15} />} onClick={() => navigate('create')}>Add Preset</Button>
          </div>
        }
      />

      <Toolbar
        start={
          <Input
            startSlot={<Search size={15} />}
            placeholder="Search presets"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            wrapperClassName={styles.searchInput}
          />
        }
        end={
          <Button
            variant="secondary"
            startIcon={<Download size={15} />}
            disabled={filtered.length === 0}
            onClick={() => exportCsv(filtered)}
          >
            Export
          </Button>
        }
      />

      {loading ? (
        <div className={styles.skeletonGrid}>
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          aria-label="Transcoding presets"
          emptyState={
            query.trim() ? (
              'No presets match your search.'
            ) : (
              <EmptyState icon={<Layers size={20} />} title="No transcoding presets" description="Add or select a preset to define live stream output quality." />
            )
          }
          footer={filtered.length ? `Showing ${filtered.length} of ${presets.length}` : undefined}
        />
      )}

      <PresetDetailDialog
        serviceId={serviceId}
        presetId={detailId}
        open={!!detailId}
        onOpenChange={(v) => { if (!v) setDetailId(null); }}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(v) => { if (!v) setPendingDelete(null); }}
        title="Delete Transcoding Preset"
        description={`This will permanently delete preset "${pendingDelete?.label || pendingDelete?.value}". This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        requireText={pendingDelete?.label || pendingDelete?.value}
        loading={deleteMut.isPending}
        onConfirm={() => { if (pendingDelete) deleteMut.mutate(pendingDelete.value); }}
      />
    </Container>
  );
}
